<template>
  <div class="ma-4">
    <v-card>
      <v-toolbar>
        <v-toolbar-title>SERVE Student Portal</v-toolbar-title>
      </v-toolbar>
      <serve-detail :serve-data="serveData" />
    </v-card>
    <v-card v-if="isEvents && hoursRemaining > 0" class="mt-4">
      <v-card-title>SERVE Event Sign Up</v-card-title>
      <v-card-text>
        <p v-if="hoursRemaining === 0">You have completed or committed to all of your hours for SERVE. Thank you!</p>
        <p v-else-if="upcomingEvents.length === 0">You are assigned to an event-based team, but there are currently no upcoming events available.</p>
        <p v-else>Below is a list of upcoming events for which you can sign-up. You have {{ hoursRemaining }} hour{{ hoursRemaining === 1 ? '' : 's' }} left to serve. Click on an event below to see more information and choose if you want to sign-up to serve for that event.</p>
        <template v-if="upcomingEvents.length > 0">
          <v-text-field v-model="filter" label="Search Events" outlined hide-details>
            <template #prepend-inner>
              <v-icon small style="transform:translateY(2px) translateX(-2px)">fal fa-search</v-icon>
            </template>
          </v-text-field>
          <v-list>
            <v-list-item v-for="{ _id, name, date, spotsRemaining, minHours, maxHours } of filteredEvents" :key="_id" @click="openSignup(_id)">
              <v-list-item-content>
                <v-list-item-title>{{ name }}</v-list-item-title>
                <v-list-item-subtitle>{{ new Date(date).toDateString() }}</v-list-item-subtitle>
                <v-list-item-subtitle>Expect to spend {{ minHours === maxHours ? minHours : `between ${minHours} and ${maxHours}` }} hours serving</v-list-item-subtitle>
                <v-list-item-subtitle>{{ spotsRemaining }} Spots Left to Fill</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-dialog v-model="eventSignupDialog" width="800">
            <v-card>
              <v-card-title>Event Sign-Up: {{ signupEvent.name }}</v-card-title>
              <v-card-text>
                <h4>{{ new Date(signupEvent.date).toDateString() }}</h4>
              </v-card-text>
              <v-card-text v-html="signupEvent.description" />
              <v-divider class="mb-3" />
              <v-card-text v-if="signupEvent.blocks.length > 1">
                <p>Please select how you would like to serve. There are {{ signupEvent.blocks.length }} options available for how to serve. You can sign up for more than one option, but make sure you don't overcommit yourself, and make sure you don't choose any overlapping options.</p>
                <v-list>
                  <v-list-item v-for="{ _id, desc, minHours, maxHours, hourOptions, slotCount, slotsFilled } of signupEvent.blocks" :key="_id" :disabled="slotsFilled >= slotCount">
                    <v-list-item-avatar style="cursor:pointer" @click="toggleBlock(_id, hourOptions)">
                      <v-icon v-if="signupBlocks.includes(_id)" color="primary">fas fa-check-square</v-icon>
                      <v-icon v-else-if="slotCount > slotsFilled">fal fa-square</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content style="cursor:pointer" @click="toggleBlock(_id, hourOptions)">
                      <v-list-item-title>{{ desc }}</v-list-item-title>
                      <v-list-item-subtitle v-if="minHours === maxHours">{{ minHours }} Hour{{ minHours === 1 ? '' : 's' }}</v-list-item-subtitle>
                      <v-list-item-subtitle v-else>{{ minHours }} to {{ maxHours }} Hours</v-list-item-subtitle>
                      <v-list-item-subtitle>{{ slotCount - slotsFilled }} Slot{{ slotCount - slotsFilled === 1 ? '' : 's' }} Remaining</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="minHours !== maxHours && slotCount > slotsFilled">
                      <v-select :items="hourOptions" label="Hours" :disabled="!signupBlocks.includes(_id)" outlined hide-details dense style="max-width:150px" @change="(v) => setHours(_id, v)" />
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
              <v-card-text v-else>
                <p>This event has a fixed requirement of {{ signupEvent.minHours }} hour{{ signupEvent.minHours === 1 ? '' : 's' }}. By signing up, you are committing to serving those hours. If you are unable to serve that many hours, please find another event where you can serve.</p>
              </v-card-text>
              <v-card-actions>
                <v-btn color="success" @click="completeSignup()">Complete Sign-Up</v-btn>
                <v-spacer />
                <v-btn @click="eventSignupDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'

export default {
  components: {
    ServeDetail: () => import('@/components/serve/detail')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const serveData = ref([])
    const serveId = ref('')
    const hoursRemaining = ref(0)
    const isEvents = ref(false)
    const upcomingEvents = ref([])
    const filter = ref('')
    const filteredEvents = computed(() => upcomingEvents.value.filter(({ name, date }) => filter.value === '' || new RegExp(filter.value, 'ig').test(name + ' ' + date + ' ' + new Date(date).toDateString())))

    onMounted(async () => {
      if (user.value.roles.includes('Employee')) {
        if (user.value.roles.includes('Student Development') || user.value.roles.includes('Technology Services')) {
          root.$router.replace('/serve/admin')
        } else {
          root.$router.replace('/serve/super')
        }
      } else {
        const aggregate = [
          { $match: { bannerId: user.value.bannerId } },
          { $unwind: {
            path: '$history',
            preserveNullAndEmptyArrays: true
          } },
          { $lookup: {
            from: 'Serve-Events',
            localField: 'history.event',
            foreignField: '_id',
            as: 'event'
          } },
          { $addFields: {
            event: { $first: '$event' }
          } },
          { $addFields: {
            'history.eventName': '$event.name',
            event: {
              $first: {
                $filter: {
                  input: '$event.students',
                  as: 'stu',
                  cond: {
                    $eq: ['$$stu.pidm', '$pidm']
                  }
                }
              }
            }
          } },
          { $addFields: { 'history.blocks': '$event.blocks' } },
          { $group: {
            _id: {
              term: '$term',
              assignment: '$assignment',
              first: '$first',
              last: '$last',
              target: '$target'
            },
            history: { $push: '$history' }
          } },
          { $addFields: {
            term: '$_id.term',
            assignment: '$_id.assignment',
            first: '$_id.first',
            last: '$_id.last',
            target: '$_id.target',
            isEvents: { $not: { $in: ['$_id.assignemt', ['Dining', 'Facilities', 'Grounds', 'Chapel', 'Off-Campus']] } }
          } },
          { $sort: { term: 1 } }
        ]
        const { data } = await root.$feathers.service('serve/student').find({ query: { aggregate } })
        serveData.value = data
        if (data.length === 0) {
          root.$store.dispatch('main/snackbar', { color: 'error', text: 'No entry was found for you in SERVE. Please contact Student Development if you should be in the SERVE system.' })
        } else {
          serveId.value = data[0]._id
          const dt = new Date()
          const activeTerm = dt.getFullYear() + (dt.getMonth() < 6 ? '05' : '60')
          const dateStr = dt.toLocaleDateString('en-CA')
          const hoursUsed = data[0].history.reduce((prev, { hours, status }) => prev + (['Completed', 'Committed'].includes(status) ? hours : 0), 0)
          hoursRemaining.value = data[0].target - hoursUsed
          if (data[0].term.includes(activeTerm) && data[0].isEvents) {
            isEvents.value = true
            const aggregate = [
              { $match: {
                term: { $in: data[0].term },
                active: true,
                regOpen: { $lte: dateStr },
                regClosed: { $gte: dateStr }
              } },
              { $unwind: '$blocks' },
              { $addFields: {
                'blocks.slotsFilled': {
                  $size: {
                    $filter: {
                      input: '$students',
                      as: 'stu',
                      cond: { $in: [{ $toString: '$blocks._id' }, '$$stu.blocks.blockId'] }
                    }
                  }
                }
              } },
              { $addFields: { 'blocks.slotsRemaining': { $subtract: ['$blocks.slotCount', '$blocks.slotsFilled'] } } },
              { $group: {
                _id: {
                  _id: '$_id',
                  name: '$name',
                  date: '$date',
                  term: '$term',
                  description: '$description',
                  regOpen: '$regOpen',
                  regClosed: '$regClosed'
                },
                blocks: { $push: '$blocks' }
              } },
              { $project: {
                _id: '$_id._id',
                name: '$_id.name',
                date: '$_id.date',
                description: '$_id.description',
                regOpen: '$_id.regOpen',
                regClosed: '$_id.regClosed',
                spotsRemaining: {
                  $reduce: {
                    input: '$blocks',
                    initialValue: 0,
                    in: { $add: ['$$value', '$$this.slotsRemaining'] }
                  }
                },
                blocks: 1
              } },
              { $match: { spotsRemaining: { $gt: 0 } } }
            ]
            const excludeEvents = data[0].history.filter(({ event }) => !!event).map(({ event }) => event)
            if (excludeEvents.length > 0) {
              aggregate.push({ $match: { _id: { $nin: excludeEvents } } })
            }
            const { data: eventData } = await root.$feathers.service('serve/event').find({ query: { aggregate } })
            upcomingEvents.value = eventData.map((row) => {
              row.blocks = row.blocks.map((block) => {
                if (block.slotsFilled >= block.slotCount) block.disabled = true
                const hourOptions = []
                for (let i = block.minHours; i <= block.maxHours; i += 0.5) {
                  hourOptions.push({ text: i + ' Hour' + (i === 1 ? '' : 's'), value: i })
                }
                return { ...block, hourOptions }
              })
              return row
            })
          }
        }
      }
    })

    const eventSignupDialog = ref(false)
    const signupEvent = ref({
      _id: '',
      name: '',
      date: '',
      description: '',
      minHours: 1,
      maxHours: 10,
      spotsRemaining: 0,
      blocks: []
    })
    const signupBlocks = ref([])
    function toggleBlock (id, hourOptions) {
      const index = signupBlocks.value.findIndex((i) => id === i)
      if (index >= 0) {
        signupBlocks.value.splice(index, 1)
      } else {
        signupBlocks.value.push(id)
        if (hourOptions.length === 1) setHours(id, hourOptions[0].value)
      }
    }
    function openSignup (id) {
      const rec = upcomingEvents.value.find(({ _id }) => _id === id)
      signupEvent.value = rec
      eventSignupDialog.value = true
    }

    const signupHours = ref([])
    function setHours (id, hours) {
      const index = signupHours.value.find(({ id: i }) => id === i)
      if (index >= 0) {
        signupHours.value.splice(index, 1, { id, hours })
      } else {
        signupHours.value.push({ id, hours })
      }
    }

    async function completeSignup () {
      const blocks = signupBlocks.value.map((blockId) => {
        const rec = signupEvent.value.blocks.find(({ _id }) => _id === blockId)
        let desc = ''
        let hours = 0
        if (rec) {
          desc = rec.desc
          if (rec.minHours !== rec.maxHours) {
            // Lookup the selected number of hours
            const hourRec = signupHours.value.find(({ id }) => id === blockId)
            if (hourRec) hours = hourRec.hours
          } else {
            hours = rec.minHours
          }
        }
        return { blockId, desc, hours }
      })
      const hours = blocks.reduce((prev, { hours }) => prev + hours, 0)
      // Add the current student to the students array of the event as Committed
      await root.$feathers.service('serve/event').patch(signupEvent.value._id, { $push: { students: {
        pidm: user.value.pidm,
        first: user.value.preferred,
        last: user.value.last,
        status: 'Committed',
        blocks
      } } })
      // Add the same thing to the student's history as Committed
      const data = await root.$feathers.service('serve/student').patch(serveId.value, { $push: { history: {
        date: new Date(signupEvent.value.date),
        desc: 'Committed to ' + signupEvent.value.name,
        addedBy: user.value.username,
        event: signupEvent.value._id,
        status: 'Committed',
        hours
      } } })
      const index = serveData.value.findIndex(({ _id }) => _id === serveId.value)
      if (index >= 0) serveData.value.splice(index, 1, data)
      const eventIndex = upcomingEvents.value.findIndex(({ _id }) => _id === signupEvent.value._id)
      if (eventIndex >= 0) {
        upcomingEvents.value.splice(eventIndex, 1)
      }
      eventSignupDialog.value = false
    }

    return {
      user,
      serveData,
      hoursRemaining,
      isEvents,
      upcomingEvents,
      filter,
      filteredEvents,
      eventSignupDialog,
      signupEvent,
      signupBlocks,
      toggleBlock,
      openSignup,
      signupHours,
      setHours,
      completeSignup
    }
  }
}
</script>
